import { useState, useRef } from "react";
import { useHomepageProducts } from "@/contexts/HomepageProductsContext";

interface Props {
  onSelect: (category: string) => void;
}

const categoryAccents: Record<string, string> = {
  escritório: "bg-blue-500",
  mochilas: "bg-violet-500",
  canecas: "bg-amber-500",
  canetas: "bg-rose-500",
  cadernos: "bg-indigo-500",
  garrafas: "bg-red-500",
  sacolas: "bg-emerald-500",
  "bolsas térmicas": "bg-cyan-500",
  chaveiros: "bg-fuchsia-500",
  nécessaire: "bg-teal-500",
};

const fallbackAccents = [
  "bg-blue-500",
  "bg-emerald-500",
  "bg-rose-500",
  "bg-amber-500",
  "bg-violet-500",
  "bg-cyan-500",
  "bg-fuchsia-500",
  "bg-red-500",
];

export default function CategoryShowcase({ onSelect }: Props) {
  const { categoryNames, showcase, categoryCounts } = useHomepageProducts();

  const categories = categoryNames
    .map((name) => {
      const s = showcase[name];
      if (!s || typeof s.imageUrl !== "string" || s.imageUrl.length === 0) return null;
      return { name, imageUrl: s.imageUrl, count: categoryCounts[name] ?? 0 };
    })
    .filter((c): c is { name: string; imageUrl: string; count: number } => c !== null);

  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleBlur = () => setTimeout(() => setShowDropdown(false), 150);

  if (categories.length === 0) return null;

  const normalize = (s: string) =>
    s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const suggestions = search
    ? categories.filter((c) => {
        const terms = search.split(/\s+/).filter(Boolean).map(normalize);
        return terms.every((t) => normalize(c.name).includes(t));
      })
    : [];

  return (
    <section className="mb-8 sm:mb-12">
      <div className="mb-4 flex items-center gap-2 sm:mb-6">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
        </span>
        <h2 className="text-lg font-bold text-text-primary sm:text-xl">
          Categorias
        </h2>
        <div className="relative ml-auto w-48 sm:w-64" ref={wrapperRef}>
          <svg className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            placeholder="Buscar categoria..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
            onFocus={() => search && setShowDropdown(true)}
            onBlur={handleBlur}
            className="w-full rounded-lg border border-border bg-bg py-2 pl-8 pr-3 text-sm text-text-primary placeholder-text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
          {showDropdown && search && suggestions.length > 0 && (
            <ul className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-lg border border-border bg-bg shadow-lg">
              {suggestions.map((cat) => (
                <li key={cat.name}>
                  <button
                    type="button"
                    onClick={() => { onSelect(cat.name); setSearch(""); setShowDropdown(false); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-primary hover:bg-surface"
                  >
                    <svg className="h-3.5 w-3.5 shrink-0 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                    </svg>
                    <span className="truncate">{cat.name}</span>
                    <span className="ml-auto shrink-0 text-[10px] text-text-muted">{cat.count}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
        {categories.map((cat, idx) => {
          const accent =
            categoryAccents[cat.name.toLowerCase()] ??
            fallbackAccents[idx % fallbackAccents.length];

          return (
            <button
              key={cat.name}
              type="button"
              onClick={() => onSelect(cat.name)}
              className="group overflow-hidden rounded-xl border border-border bg-bg text-left transition-all duration-normal hover:border-border-hover hover:shadow-card-hover active:scale-[0.98]"
            >
              <div className="relative flex items-center justify-center overflow-hidden bg-surface p-2 sm:p-3">
                <img
                  src={cat.imageUrl}
                  alt={cat.name}
                  className="max-h-40 w-full object-contain transition-transform duration-normal group-hover:scale-105 sm:max-h-52"
                  loading="lazy"
                />
              </div>

              <div className="relative px-2.5 py-2 sm:px-3 sm:py-3">
                <div className={`absolute inset-x-0 top-0 h-0.5 ${accent}`} />
                <div className="flex items-center justify-between gap-1">
                  <span className="text-[11px] font-semibold text-text-primary sm:text-sm">
                    {cat.name}
                  </span>
                  <span className="shrink-0 rounded-full bg-surface px-1.5 py-0.5 text-[9px] font-medium text-text-muted">
                    {cat.count}
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
